import { useState, useRef, useEffect } from 'preact/hooks'

export function ViewSelector({ currentView, onViewChange, starredCount, compact = false, excludeMyPlan = false, lastMainView = 'agenda' }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  const viewOptions = [
    {
      id: 'agenda',
      label: 'Schedule',
      icon: '📋',
      description: 'Browse all sessions'
    },
    {
      id: 'timeline',
      label: 'Timeline',
      icon: '⏱️',
      description: 'Sessions by time blocks'
    },
    {
      id: 'visual-timeline',
      label: 'Visual Timeline',
      icon: '📊',
      description: 'Gantt chart view'
    },
    // Only include My Plan if not excluded
    ...(!excludeMyPlan ? [{
      id: 'my-plan',
      label: 'My Plan',
      icon: '⭐',
      description: `${starredCount} starred session${starredCount !== 1 ? 's' : ''}`,
      badge: starredCount > 0 ? starredCount : null
    }] : [])
  ]

  // When My Plan is excluded and currently active, show the last main view instead of defaulting to Schedule
  const currentViewOption = excludeMyPlan && currentView === 'my-plan' 
    ? viewOptions.find(option => option.id === lastMainView) || viewOptions[0] // Use lastMainView when My Plan is active
    : viewOptions.find(option => option.id === currentView) || viewOptions[0]

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleViewSelect = (viewId) => {
    onViewChange(viewId)
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Current View Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 transition-all duration-200
          bg-gradient-to-r from-editions-purple to-editions-purple-light 
          text-white border-editions-purple-light/50 shadow-lg shadow-editions-purple/25
          hover:shadow-editions-purple/40 transform hover:scale-105
          text-sm
        `}
        aria-label={`Current view: ${currentViewOption?.label}. Click to change view.`}
      >
        <span className="text-sm">
          {currentViewOption?.icon}
        </span>
        <span className="text-sm font-medium">
          {currentViewOption?.label}
        </span>
        {currentViewOption?.badge && (
          <span className="bg-editions-orange text-white text-xs px-1.5 py-0.5 rounded-full min-w-0">
            {currentViewOption.badge}
          </span>
        )}
        <svg 
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-editions-dark/95 backdrop-blur-lg border border-white/20 rounded-xl shadow-xl z-50">
          <div className="p-2">
            {viewOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => handleViewSelect(option.id)}
                className={`
                  w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 text-left
                  ${option.id === currentView
                    ? 'bg-editions-purple/20 text-white border border-editions-purple/40'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }
                `}
              >
                <span className="text-lg flex-shrink-0">{option.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{option.label}</div>
                  <div className="text-xs text-gray-400 truncate">{option.description}</div>
                </div>
                {option.badge && (
                  <span className="bg-editions-orange text-white text-xs px-2 py-1 rounded-full">
                    {option.badge}
                  </span>
                )}
                {option.id === currentView && (
                  <div className="w-2 h-2 bg-editions-purple rounded-full flex-shrink-0"></div>
                )}
              </button>
            ))}
          </div>
          
          {/* Quick Tips */}
          <div className="border-t border-white/10 p-3">
            <div className="text-xs text-gray-400">
              💡 Switch between different ways to view{excludeMyPlan ? '' : ' and organize'} your conference schedule
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 